import { Play } from 'models/tagging/Play';

import { AutomaticTypes, CallTypes, Dexterity, KOrBB } from '@trackman/baseball-shared-components';

export interface IPlayConsequence {
  shouldProgressBatter: boolean;
  shouldProgressHalfInning: boolean;
  nextPlayBalls: number;
  nextPlayOuts: number;
  nextPlayStrikes: number;
  isKOrBb: KOrBB;
}
export const getPlayConsequences = (play: Play, isNewInning = false): IPlayConsequence => {
  const shouldProgressHalfInning = getShouldProgressHalfInning(play);
  const shouldProgressBatter = getShouldProgressBatter(play, shouldProgressHalfInning, isNewInning);

  const consequence = {
    shouldProgressBatter,
    shouldProgressHalfInning,
    isKOrBb: getIsKOrBb(play, shouldProgressBatter),
    nextPlayBalls: getNextPlayBalls(play, shouldProgressHalfInning, shouldProgressBatter),
    nextPlayStrikes: getNextPlayStrikes(play, shouldProgressHalfInning, shouldProgressBatter),
    nextPlayOuts: getNextPlayOuts(play, shouldProgressHalfInning),
  };
  return consequence;
};

const isStrikeoutInLastPlayPreviousInning = (isNewInning: boolean, pitchCall: string, strikes: number): boolean => {
  return isNewInning && (pitchCall === CallTypes.StrikeCalled || pitchCall === CallTypes.StrikeSwinging) && strikes === 3;
};

export const getShouldProgressBatter = (play: Play, shouldProgressHalfInning: boolean, isNewInning: boolean): boolean => {
  if (isStrikeoutInLastPlayPreviousInning(isNewInning, play.tag.pitchCall, play.strikes.total)) {
    return true;
  }

  if (isNewInning && play.tag.pitchCall !== CallTypes.InPlay) {
    return false;
  }

  return (
    shouldProgressHalfInning ||
    play.strikes.total >= 3 ||
    play.balls.total >= 4 ||
    play.tag.pitchCall === CallTypes.HitByPitch ||
    play.tag.pitchCall === CallTypes.InPlay ||
    play.tag.pitchCall === AutomaticTypes.AutomaticWalk ||
    play.tag.pitchCall === CallTypes.BallIntentional
  );
};

export const getShouldProgressHalfInning = (play: Play): boolean => {
  return play.outs.total >= 3 || (play.outs.total === 2 && play.strikes.total >= 3);
};

export const getNextPlayBalls = (play: Play, shouldProgressHalfInning: boolean, shouldProgressBatter: boolean): number => {
  if (shouldProgressHalfInning || shouldProgressBatter) {
    return 0;
  }
  return play.balls.total;
};

export const getNextPlayStrikes = (play: Play, shouldProgressHalfInning: boolean, shouldProgressBatter: boolean): number => {
  if (shouldProgressHalfInning || shouldProgressBatter) {
    return 0;
  }
  return play.strikes.total;
};

export const getNextPlayOuts = (play: Play, shouldProgressHalfInning: boolean): number => {
  if (shouldProgressHalfInning) {
    return 0;
  }
  return play.outs.total + (play.strikes.total >= 3 ? 1 : 0);
};

export const getIsKOrBb = (play: Play, shouldProgressBatter: boolean): KOrBB => {
  // Skip counting balls/strikes if pitch call was Hit by Pitch
  if (play.tag.pitchCall === CallTypes.HitByPitch) {
    return KOrBB.Undefined;
  }

  if (play.tag.pitchCall === CallTypes.BallIntentional) {
    return KOrBB.Walk;
  }

  if (shouldProgressBatter) {
    if (play.strikes.total >= 3) {
      return KOrBB.Strikeout;
    }
    if (play.balls.total >= 4 || play.tag.pitchCall === AutomaticTypes.AutomaticWalk) {
      return KOrBB.Walk;
    }
  }
  return KOrBB.Undefined;
};

export const getBatterHandednessForTag = (play: Play): string => {
  if (play.batter.battingHandedness && play.batter.battingHandedness === Dexterity.Both) {
    if (play.pitcher.pitchingHandedness && play.pitcher.pitchingHandedness !== Dexterity.Both) {
      return play.pitcher.pitchingHandedness === Dexterity.Right ? Dexterity.Left : Dexterity.Right;
    }
    return Dexterity.Undefined;
  }
  return play.batter.battingHandedness;
};

export const getPitcherHandednessForTag = (play: Play): string => {
  if (play.pitcher.pitchingHandedness && play.pitcher.pitchingHandedness === Dexterity.Both) {
    if (play.batter.battingHandedness && play.batter.battingHandedness !== Dexterity.Both) {
      return play.batter.battingHandedness === Dexterity.Right ? Dexterity.Left : Dexterity.Right;
    }
    return Dexterity.Undefined;
  }
  return play.pitcher.pitchingHandedness;
};

export const isHomeTeamBatting = (isTopInning: boolean): boolean => !isTopInning;

export const getNextInning = (currentNumber: number, currentIsTop: boolean, moveUpwards: boolean) => {
  if (moveUpwards) {
    return {
      number: currentIsTop ? currentNumber : currentNumber + 1,
      isTop: !currentIsTop,
      playsReversed: [],
    };
  }
  return {
    number: currentIsTop ? currentNumber - 1 : currentNumber,
    isTop: !currentIsTop,
    playsReversed: [],
  };
};
